<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
import { useHome } from '~/composables/useHome'

const { getTestimonials } = useHome()
const { data: rawTestimonials } = await useAsyncData('home-testimonials', () => getTestimonials())

const testimonials = computed(() => {
  if (!rawTestimonials.value) return []
  const sorted = [...rawTestimonials.value].sort((a, b) => a.orderIndex - b.orderIndex)
  // Duplicate for infinite loop
  return [...sorted, ...sorted]
})

const selectedTestimonial = ref<any>(null)
const isModalOpen = ref(false)

const openTestimonial = (testimonial: any) => {
  selectedTestimonial.value = testimonial
  isModalOpen.value = true
}

const closeTestimonial = () => {
  isModalOpen.value = false
}

const getIcon = (type: string) => {
  switch (type) {
    case 'Student': return 'user-graduate'
    case 'Alumni': return 'graduation-cap'
    case 'Speaker': return 'chalkboard-teacher'
    case 'Company': return 'building'
    default: return 'user-graduate'
  }
}

const getLabel = (type: string) => {
  switch (type) {
    case 'Student': return 'Témoignage Étudiant'
    case 'Alumni': return 'Témoignage Alumni'
    case 'Speaker': return 'Pro de l\'industrie'
    case 'Company': return 'Studio Partenaire'
    default: return 'Témoignage'
  }
}
</script>

<template>
  <section class="py-section-py bg-background relative overflow-hidden transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10">
      <UiSectionHeader 
        titlePrefix="LA PAROLE À NOTRE"
        titleHighlight="COMMUNAUTÉ"
        subtitle="Étudiants, diplômés, intervenants et partenaires : ils partagent leur expérience OXO."
        class="mb-section-header-mb"
      />

      <div v-if="testimonials && testimonials.length > 0">
        <UiCarousel 
          :gradient-edges="true" 
          auto-scroll 
          infinite 
          :auto-scroll-speed="1"
          :paused="isModalOpen"
          container-tag="ul"
        >
          <li 
            v-for="testimonial in testimonials" 
            :key="testimonial.id"
            class="w-[230px] h-[192px] md:w-[336px] md:h-[280px] lg:w-[384px] lg:h-[320px] shrink-0 snap-start list-none"
          >
            <article 
              @click="openTestimonial(testimonial)"
              class="bg-oxo-card-bg/50 backdrop-blur-xs p-4 md:p-10 rounded-2xl md:rounded-4xl border border-white/5 flex flex-col w-full h-full cursor-pointer hover:bg-oxo-card-bg/80 hover:border-oxo-red/30 hover:scale-[1.02] transition-all duration-300 group"
            >
              <!-- Header -->
              <div class="flex items-start justify-between mb-3 md:mb-8">
                <div class="flex items-center gap-2 md:gap-5">
                     <div class="relative shrink-0 w-10 h-10 md:w-16 md:h-16 rounded-lg md:rounded-2xl bg-white/5 flex items-center justify-center border border-white/10 group-hover:border-oxo-red/50 transition-colors overflow-hidden">
                        <NuxtImg loading="lazy"
                          v-if="testimonial.image"
                          :src="testimonial.image"
                          :alt="testimonial.imageAlt || testimonial.author"
                          class="w-full h-full object-cover"
                          sizes="40px md:64px"
                        />
                        <i :style="faIconStyle('fas fa-' + getIcon(testimonial.authorType))" v-else :class="'fas fa-' + getIcon(testimonial.authorType) + ' text-2xl text-foreground-dim group-hover:text-foreground transition-colors'" aria-hidden="true"></i>
                   </div>
                  <div>
                    <h3 class="font-display font-black text-xs md:text-base text-oxo-red uppercase leading-none mb-1 md:mb-2 group-hover:text-foreground transition-colors">
                        {{ testimonial.author }}
                    </h3>
                    <p class="text-[10px] text-foreground-dim font-mono uppercase tracking-[0.2em]">
                      {{ testimonial.role }}
                    </p>
                  </div>
                </div>
                <div class="text-oxo-red/20 group-hover:text-oxo-red transition-colors duration-500">
                  <i class="fas fa-quote-right text-xl md:text-3xl" aria-hidden="true"></i>
                </div>
              </div>

               <!-- Content -->
               <div class="relative grow mb-3 md:mb-6 overflow-hidden">
                 <p class="text-foreground text-xs md:text-base leading-snug font-light line-clamp-2">
                  {{ testimonial.content }}
                </p>
              </div>

              <!-- Divider -->
              <div class="w-full h-px bg-linear-to-r from-transparent via-white/10 to-transparent mb-3 md:mb-6 group-hover:via-oxo-red/30 transition-all duration-500"></div>

              <!-- Footer -->
                <div class="flex justify-between items-center text-[10px] font-mono uppercase tracking-[0.15em] text-foreground-dim">
                  <div class="flex items-center gap-3">
                    <span class="w-1.5 h-1.5 rounded-full bg-oxo-red animate-pulse"></span>
                    <span class="font-bold text-foreground">{{ getLabel(testimonial.authorType) }}</span>
                  </div>
                  
                  <!-- Globe link icon -->
                  <a v-if="testimonial.authorLink" :href="testimonial.authorLink" target="_blank" @click.stop class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-foreground-dim hover:text-oxo-red hover:border-oxo-red/50 hover:bg-oxo-red/5 transition-all duration-300" :aria-label="'Voir le profil de ' + testimonial.author">
                    <i class="fas fa-globe text-sm" aria-hidden="true"></i>
                  </a>
                </div>
            </article>
          </li>
        </UiCarousel>
      </div>
      
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="isModalOpen" class="fixed inset-0 z-100 flex items-center justify-center p-4 md:p-12 overflow-y-auto bg-background/95 backdrop-blur-xl">
          <div class="absolute inset-0" @click="closeTestimonial"></div>
          
          <div class="relative w-full max-w-4xl bg-oxo-card-bg border border-white/10 rounded-[3rem] shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh]">
            <div class="md:w-1/3 bg-background p-8 md:p-12 flex flex-col items-center justify-center text-center border-b md:border-b-0 md:border-r border-white/5">
                <div class="relative mb-6">
                     <div class="w-32 h-32 md:w-48 md:h-48 rounded-4xl bg-white/5 flex items-center justify-center ring-4 ring-oxo-red/20 overflow-hidden">
                        <NuxtImg loading="lazy"
                          v-if="selectedTestimonial?.image"
                          :src="selectedTestimonial.image"
                          :alt="selectedTestimonial.imageAlt || selectedTestimonial.author"
                          class="w-full h-full object-cover"
                          sizes="128px md:192px"
                        />
                        <i :style="faIconStyle('fas fa-' + getIcon(selectedTestimonial?.authorType))" v-else :class="'fas fa-' + getIcon(selectedTestimonial?.authorType) + ' text-6xl text-foreground-dim'" aria-hidden="true"></i>
                     </div>
                    <div class="absolute -bottom-4 -right-4 bg-oxo-red text-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                        <i class="fas fa-quote-left" aria-hidden="true"></i>
                    </div>
                </div>
                <h2 class="font-display font-black text-lg md:text-2xl text-foreground uppercase mb-2">{{ selectedTestimonial?.author }}</h2>
                <p class="text-xs font-mono text-foreground-dim uppercase tracking-[0.2em] mb-6">{{ selectedTestimonial?.role }}</p>
                <a v-if="selectedTestimonial?.authorLink" :href="selectedTestimonial?.authorLink" target="_blank" class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-foreground-dim hover:text-oxo-red hover:border-oxo-red/50 hover:bg-oxo-red/5 transition-all text-[10px] font-mono uppercase tracking-widest mt-4">
                  <i class="fas fa-globe" aria-hidden="true"></i>
                  Voir le profil
                </a>
            </div>

            <div class="flex-1 p-8 md:p-16 overflow-y-auto custom-scrollbar">
                <div class="prose prose-invert prose-lg max-w-none">
                    <p class="text-foreground font-light leading-relaxed whitespace-pre-wrap text-lg italic">
                        "{{ selectedTestimonial?.content }}"
                    </p>
                </div>
            </div>

            <button 
                @click="closeTestimonial"
                class="absolute top-6 right-6 w-12 h-12 rounded-2xl bg-white/5 hover:bg-oxo-red text-foreground hover:text-white transition-all flex items-center justify-center group z-20"
            >
                <i class="fas fa-times text-xl group-hover:scale-110 transition-transform" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}
</style>
